import { format } from 'date-fns';
import { useFormContext } from 'react-hook-form';
import { useEffect, useRef } from 'react';

import { useStoreActions } from '~/store/hooks';
import { useAddClientAllocationContext } from '../../context/AddClientAllocationContext';
import { dateRequestFormat } from '~/ui/constants/dateFormat';
import { IAddAllocationFormValues } from '../../types';

type IClientValidate = {
  teamId: number;
  clientId: number;
};

interface IProps {
  clientsAllocations: IClientValidate[];
  userId: number;
}

const useAddAllocationAvailability = ({ clientsAllocations, userId }: IProps): void => {
  const { actTeamId } = useAddClientAllocationContext();
  const { onValidateAvailability } = useStoreActions(actions => actions.clientAllocation);
  const { watch } = useFormContext<IAddAllocationFormValues>();
  const dateRange = watch('dateRange');

  const { clinicId } = useAddClientAllocationContext();

  // Include all clients regardless of team — use actTeamId as fallback (same as old form)
  const newClients = clientsAllocations.map(client => ({
    teamId: client.teamId ?? Number(actTeamId),
    userId,
    clientId: client.clientId,
  }));

  const from = dateRange[0];
  const to = dateRange[1];

  const prevCountRef = useRef(newClients.length);
  const prevFromRef = useRef(from);
  const prevToRef = useRef(to);

  useEffect(() => {
    const countChanged = newClients.length !== prevCountRef.current;
    const dateChanged = from !== prevFromRef.current || to !== prevToRef.current;

    if ((countChanged || dateChanged) && from && to) {
      onValidateAvailability({
        clinicId: Number(clinicId),
        payload: {
          dateRange: {
            from: format(new Date(from), dateRequestFormat),
            to: format(new Date(to), dateRequestFormat),
          },
          clientAllocations: newClients,
        },
      });
    }

    prevCountRef.current = newClients.length;
    prevFromRef.current = from;
    prevToRef.current = to;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newClients.length, from, to]);
};

export default useAddAllocationAvailability;
